import React, { useCallback } from 'react'
import styled from 'styled-components'
import { ConstelaConfigurationModule } from '../../types/ConstelaConfiguration'
import { ServiceData } from '../../types/ModuleData'
import { reorderArray } from '../store/actions/configurationActions'
import { useBreakpoint, useInspirationDetail } from '../store/hooks/uiHooks'
import { useCurrentDragType } from './dnd/DragDropContext'
import Droppable from './dnd/Droppable'
import { DroppableHighlight } from './dnd/DroppableWrap'
import PreviewModule from './preview/PreviewModule'

export type ServicesListProps = {
  availableServices: ServiceData[]
  services: ConstelaConfigurationModule[]
  setServices: (nextServices: ConstelaConfigurationModule[]) => unknown
  className?: string
}

const ServiceListItem = styled<
  React.FC<{
    service?: ServiceData
  }>
>(({ service, ...props }) => {
  const { closeDetail, showDetail, detailModule } = useInspirationDetail()
  const onClick = useCallback(() => {
    if (detailModule?.id === service?.id) {
      closeDetail()
    } else {
      showDetail(service?.id)
    }
  }, [closeDetail, detailModule?.id, service?.id, showDetail])

  return (
    <div {...props} onClick={onClick} data-cs-ignore-close="moduleDetail">
      <PreviewModule module={service} scale={1} />
    </div>
  )
})``

const VisualAnchor = styled.div`
  width: 50px;
  height: 15px;
  border: 1px solid transparent;
  border-bottom-color: white;
  border-left-color: white;
  position: absolute;
  top: 100%;
  left: 40px;

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 100%;
    width: 15px;
    height: 15px;
    background-color: #fff;
    margin: -7px;
    border-radius: 100%;
  }
`

const ServicesList = styled<React.FC<ServicesListProps>>(({ services, availableServices, setServices, ...props }) => {
  const enableDragDrop = useBreakpoint('tablet')
  const updateServices = useCallback(
    (item: string, from: number | undefined, to: number | undefined) => {
      setServices(reorderArray(services, { id: item }, from, to))
    },
    [services, setServices]
  )
  const acceptsItem = useCallback(
    (args: { draggableId: string; item: string; droppableSourceId: string; droppableTargetId: string }) => {
      if (args.droppableSourceId === args.droppableTargetId) {
        return true
      }
      return !services.some((x) => x.id === args.item)
    },
    [services]
  )

  const show = useCurrentDragType() === 'service' || services.length > 0

  return (
    <div {...props} className={[props.className ?? '', !show ? 'cs-fade-hidden' : ''].join(' ')}>
      <Droppable
        type="service"
        inverted
        items={services.map((x) => x.id)}
        disabled={!enableDragDrop}
        updateItems={updateServices}
        acceptsItem={acceptsItem}
      >
        {(item, i, items, provided) => {
          return (
            <ServiceListItem service={availableServices.find((x) => x.id === item)} {...provided.dragHandleProps} />
          )
        }}
      </Droppable>
      <VisualAnchor />
    </div>
  )
})`
  width: 80px;
  min-height: 80px;
  border: 1px dashed #fff;
  background-color: #00000033;
  border-radius: 5px;
  transition: opacity 0.3s;

  &.cs-fade-hidden {
    opacity: 0;
  }

  ${DroppableHighlight} {
    margin: -1px;
    padding: 0;
    min-height: 82px;
    padding: 8px;

    &::before {
      border-radius: 5px;
    }
  }
`

export default ServicesList
